import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { LuUtensils, LuMapPin, LuCreditCard } from "react-icons/lu";
import { resetConfirmSubscription, fetchRestaurantDetails } from "../actions/restaurantActions";
import { fetchSubscriptionPackages } from "../actions/userActions";
import { fetchReservationDeactivate } from "../actions/reservationsActions";
import { planNameForRestaurant } from "../utils/planNames";
import RestaurantsHeader from "../components/RestaurantsHeader";
import Loader from "../components/Loader";
import Messages from "../components/Messages";
import { OverviewButton } from "../components/overview/overviewStyles";

/* The restaurant home does two things: create restaurants (the header's "Novo
   restaurante") and manage each restaurant's plan. The rest moved to where it
   belongs - the booking link, a customer booking, the widget and deleting the
   restaurant to Informações (screens/RestaurantInfoUpdate.js), and the
   statistics to their own page (screens/RestaurantStatistics.js). */

const Page = styled.div`
  width: 100%; min-width: 0; min-height: 100%; padding: 24px 28px 36px; background: #fff;
  @media (max-width: 768px) { padding: 16px 12px 28px; }
`;
const Subtitle = styled.p`color: #77817e; font-size: 13px; margin: -12px 0 22px;`;
const RestaurantChoices = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px; padding-bottom: 22px; border-bottom: 1px solid #edf0ee;
`;
const RestaurantPill = styled.button`
  display: inline-flex; align-items: center; gap: 9px; min-height: 42px; max-width: 100%;
  padding: 5px 16px 5px 6px; border-radius: 24px; font-size: 13px; font-weight: 600;
  border: 1px solid ${({ $selected }) => $selected ? "#ee754c" : "#e6ece9"};
  background: ${({ $selected }) => $selected ? "#fff4ee" : "#fff"};
  color: ${({ $selected }) => $selected ? "#c85530" : "#52655f"};
  > span:first-child { display: grid; place-items: center; width: 30px; height: 30px; border-radius: 50%;
    flex-shrink: 0; background: ${({ $selected }) => $selected ? "#ee754c" : "#f1f5f3"};
    color: ${({ $selected }) => $selected ? "#fff" : "#708a82"}; }
  > span:last-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &:hover { border-color: #ee754c; }
`;
const RestaurantHeading = styled.section`
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px;
  padding: 22px 0;
  h2 { font-size: 20px; font-weight: 650; color: #344b43; margin: 0; overflow-wrap: anywhere; }
  p { display: flex; align-items: center; gap: 5px; margin: 6px 0 0; color: #788580; font-size: 12px; }
`;
const NameRow = styled.div`display: flex; align-items: center; flex-wrap: wrap; gap: 10px;`;
const PlanBadge = styled.span`font-size: 11px; font-weight: 600; padding: 4px 8px; border-radius: 5px; color: #627c73; background: #f0f5f2;`;
const Actions = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
  @media (max-width: 576px) { width: 100%; }
`;

export default function RestaurantInfo() {
  const { t } = useTranslation();
  const { restaurants, isLoading, error } = useSelector(state => state.restaurantList);
  const { managerInfo } = useSelector(state => state.crudManager);
  const currentRestaurant = useSelector(state => state.restaurantDetails?.restaurants);
  const { getSubscriptionPackages } = useSelector(state => state.subscriptionPackagesStore || {});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);
  const list = Array.isArray(restaurants) ? restaurants : [];
  const restaurant = list.find(item => String(item.id) === String(selectedId ?? currentRestaurant?.id)) || list[0];
  const active = ["active", "trialling", "trialing"].includes(restaurant?.subscription_status);
  // "Plano ativo" alone left the owner guessing which one: name it.
  const planName = restaurant ? planNameForRestaurant(restaurant, getSubscriptionPackages, t) : "";
  const planState = !active ? "inactive" : restaurant?.subscription_status === "active" ? "active" : "trial";
  const planBadgeText = planName && planState !== "inactive"
    ? t(`restaurantOverview.${planState}Named`, { plan: planName })
    : t(`restaurantOverview.${planState}`);

  useEffect(() => {
    dispatch(fetchReservationDeactivate());
    if (managerInfo?.first_time_password_change === false) navigate("/profile/update/first_time");
  }, [dispatch, managerInfo, navigate]);
  useEffect(() => { dispatch(resetConfirmSubscription()); }, [dispatch]);
  useEffect(() => { dispatch(fetchSubscriptionPackages()); }, [dispatch]);

  const managePlan = () => {
    dispatch(fetchRestaurantDetails(restaurant.id));
    navigate(active ? `/subscription_management/${restaurant.id}` : `/pick_plan_and_pay/${restaurant.id}`);
  };

  return <Page>
    <RestaurantsHeader />
    <Subtitle>{t("restaurantOverview.homeSubtitle")}</Subtitle>
    {isLoading ? <Loader /> : error ? <Messages variant="danger">{error}</Messages> : restaurant ? <>
      <RestaurantChoices role="group" aria-label={t("restaurantOverview.selectRestaurant")}>
        {list.map(item => <RestaurantPill type="button" key={item.id} $selected={item.id === restaurant.id}
          aria-pressed={item.id === restaurant.id} onClick={() => { setSelectedId(item.id); dispatch(fetchRestaurantDetails(item.id)); }}>
          <span aria-hidden="true"><LuUtensils size={15} /></span><span>{item.restaurant_name}</span>
        </RestaurantPill>)}
      </RestaurantChoices>
      <RestaurantHeading>
        <div>
          <NameRow><h2>{restaurant.restaurant_name}</h2><PlanBadge>{planBadgeText}</PlanBadge></NameRow>
          {restaurant.adress && <p><LuMapPin aria-hidden="true" />{restaurant.adress}</p>}
        </div>
        <Actions>
          <OverviewButton type="button" $primary onClick={managePlan}><LuCreditCard />{t("ui.screens.homeScreen.gerirPlano")}</OverviewButton>
        </Actions>
      </RestaurantHeading>
    </> : <p>{t("restaurantOverview.noRestaurants")}</p>}
  </Page>;
}
